import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";

import styles from '../../../styles/Components/CMSSearchResults.module.css';
import Loader from "../../Loader";
import { setSubRoute } from "../../../redux/actions/navigation";
import { getBuyersGuideByID } from '../../../redux/actions/buyersGuide';
import { getBuyersGuideStageUrl } from '../../../helpers/getBuyersGuideStageUrl'

const ManageContentIcon = '/static/img/SvgIcons/manage-content-red.svg';

const CMSSearchResults = ({
    searchTerm,
    setSearchTerm,
    viewingSuggestion, 
    setViewingSuggestion,
    searchRef,
    searching, 
    noResults,
    setSubRoute,
    getBuyersGuideByID,
    cmsSearch: { cmsSearchResults }
}) => {
    const navigate = useNavigate()
    
    const [displayList, setDisplayList] = useState([]);
    const [resultsWithData, setResultsWithData] = useState([]);
                                  
    const handleArticleClick = (article) => {
        const specificArticleTypes = [
            'Case Study', 
            'Research', 
            'Marketing Materials',
            'CE',
        ]
        
        const articleType = article.article_type

        const articleRoute = specificArticleTypes.includes(articleType) ? articleType.replaceAll(' ', '-').toLowerCase() : articleType === 'More Information' ? 'information' : 'other'
        
        navigate(`/onsite-content/${articleRoute}?edit=${article.article_id}`)
    }

    const handleReviewClick = (path, subRoute) => {
        setSubRoute(subRoute)
        navigate(path)
    }

    const handleBuyersGuideClick = (buyersGuide) => {
        
        getBuyersGuideByID(buyersGuide.buyers_guide_id)
        navigate(`/buyers-guide/${getBuyersGuideStageUrl(buyersGuide.curr_stage)}`)
    }

    const handleCategoryClick = (category) => {
        const isTopCat = !category.parent_category_id ? true : false
        navigate(`/onsite-content/categories?edit=${category.category_id}&top_cat=${isTopCat}`)
    }

    const contentDictionary = {
        Articles: {
            getName: data => data.title,
            icon: "/static/img/SvgIcons/contentArticle-red.svg",
            action: (result) => handleArticleClick(result),
        },
        Blogs: {
            getName: data => data.title,
            icon: "/static/img/SvgIcons/contentBlog-red.svg",
            action: (result) => navigate(`/onsite-content/blogs?edit=${result.entryId}`),
        },
        'Product Videos': {
            getName: data => data.name,
            icon: "/static/img/SvgIcons/contentVideo-red.svg",
            action: (result) => navigate(`/onsite-content/videos?edit=${result.video_id}`),
        },
        Evaluations: {
            getName: data => data.Product?.name,
            icon: "/static/img/SvgIcons/evalIcon.svg",
            action: (result) => navigate(`/onsite-content/evaluations?edit=${result.product_evaluation_id}`),
        },
        Categories: {
            getName: data => data.name,
            icon: "/static/img/SvgIcons/contentArticle-red.svg",
            action: (result) => handleCategoryClick(result),
        },
        Manufacturers: {
            getName: data => data.name,
            icon: "/static/img/SvgIcons/contentArticle-red.svg",
            action: (result) => navigate(`/onsite-content/manufacturers?edit=${result.manufacturer_id}`),
        },
        Products: {
            getName: data => data.name,
            icon: "/static/img/SvgIcons/contentArticle-red.svg",
            action: (result) => navigate(`/onsite-content/products?edit=${result.product_id}`),
        },
        'Product Reviews': {//
            getName: data => data.review_title,
            icon: "/static/img/SvgIcons/contentArticle-red.svg",
            action: (result) => handleReviewClick(`/onsite-content/reviews?edit=${result.review_id}`, `products`),
        },
        'Manufacturer Reviews': {//
            getName: data => data.review_title,
            icon: "/static/img/SvgIcons/contentArticle-red.svg",
            action: (result) => handleReviewClick(`/onsite-content/reviews?edit=${result.review_id}`, `manufacturers`),
        },
        Promotions: {
            getName: data => data.title,
            icon: "/static/img/SvgIcons/contentArticle-red.svg",
            action: (result) => navigate(`/onsite-content/promotions?edit=${result.promotion_id}`),
        },
        'Buyers Guide': {
            getName: data => data.version_name,
            icon: "/static/img/SvgIcons/contentArticle-red.svg",
            action: (result) => handleBuyersGuideClick(result),
        },
        'Team Members': {
            getName: data => `${data.first_name} ${data.last_name}`,
            icon: "/static/img/SvgIcons/contentArticle-red.svg",
            action: (result) => navigate(`/tools/ims-team-members?edit=${result.team_data_id}`),
        },
        'Go To': {
            getName: data => data.name,
            action: (result) => navigate(result.path)
        }
    }

    // GET MATCHING TERMS FROM NAME
    const getTerms = (name, searchTerm, wordLen) => {
        let word;
        let arrayOfWordsInName = name.replace(/[,+()$~%'":*<>{}]/g, '').split(" ")

        if (arrayOfWordsInName.length <= wordLen) {
            word = name.toLowerCase()
        }
        else {
            let runSearch = true;
            let i = 0;
            const searchTermLen = searchTerm.split(" ").length
            
            while (runSearch) {
                
                //if search term is more than two words
                if (searchTermLen > 1) {
                    let term = "";
                    for (let n = 0; n < searchTermLen; n++) {
                        term += arrayOfWordsInName[i + n] + " "
                    }

                    if (term.toLowerCase().includes(searchTerm.replace(/[,+()$~%'":*<>{}]/g, '').toLowerCase())) {
                        word = name.split(' ').splice(i, wordLen).join(" ").toLowerCase()
                        runSearch = false
                    }
                    i++
                }
                // check for search term that is only one word
                else {
                    if (arrayOfWordsInName[i] && arrayOfWordsInName[i].toLowerCase().includes(searchTerm.replace(/[,+()$~%'":*<>{}]/g, '').toLowerCase())) {
                        
                        word = name.split(' ').splice(i, wordLen).join(" ").toLowerCase()
                        runSearch = false
                    } else if (!arrayOfWordsInName[i]) {
                        runSearch = false
                    }
                    i++
                }

                if (!arrayOfWordsInName[i]) {
                    runSearch = false
                }
            }
        }
        return (word)
    }

    // SORT ARRAY OF WORDS BY NUMBER OF TIMES THEY OCCUR
    const getMostUsedWords = (array) => {
        if (!(array?.length > 0)) {
            return array
        }

        let wordCount = {}

        array.forEach((word) => {
            wordCount = {
                ...wordCount,
                [word]: wordCount[word] ? wordCount[word] + 1 : 1
            }
        })
        
        // SORT BY TOTAL COUNT
        const sortedWords = Object.entries(wordCount).sort((a, b) => b[1] - a[1])

        // RETURN ARRAY OF WORDS
        return sortedWords.map(entry => entry[0])
    }


    useEffect(() => {
        if (cmsSearchResults && Object.entries(cmsSearchResults)?.length > 0) {
            let results = []

            let arrShort = []
            let arrLong = []
            for (let [contentType, resultsArray] of Object.entries(cmsSearchResults)) {
                if (!(resultsArray?.length > 0)) {
                    continue
                }

                // GENERATE SEARCH SUGGESTIONS
                for (let result of resultsArray) {
                    let name = contentDictionary[contentType].getName(result)
                    
                    if (contentType === 'Users' && !name.includes(searchTerm)) {
                        if (result.user && result.user.toLowerCase().includes(searchTerm.toLowerCase())) {
                            name = result.user
                        } else if (result.email && result.email.toLowerCase().includes(searchTerm.toLowerCase())) {
                            name = result.email
                        }
                    }

                    if (!name) {
                        continue
                    }
                    
                    const shortTerm = getTerms(name, searchTerm, searchTerm.split(' ').length)

                    if (shortTerm) {
                        arrShort.push(shortTerm)
                    }

                    const longTerm = getTerms(name, searchTerm, searchTerm.split(' ').length + 1)

                    if (longTerm) {
                        arrLong.push(longTerm)
                    }
                }

                // STORE SEARCH RESULTS
                results.push([
                    contentType, resultsArray
                ])
            }

            setDisplayList((getMostUsedWords([...arrShort, ...arrLong])).slice(0, 10))
            setResultsWithData(results)
        } else if (resultsWithData?.length > 0) {
            setResultsWithData([])
        }
    }, [cmsSearchResults])


    const handleClickResult = (result, contentName) => {
        const contentInfo = contentDictionary[contentName]
        if (!(contentInfo && contentInfo.action)) {
            return
        }

        contentInfo.action(result)
        setSearchTerm('')

        if (searchRef?.current?.blur) {
            searchRef.current.blur()
        }
    }

    const focusSearchBar = () => {
        if (searchRef?.current?.focus) {
            searchRef.current.focus()
        }
    }

    const handleClickSuggestion = (name) => {
        setSearchTerm(name)
        setViewingSuggestion(true)

        
        // SEARCH RESULT BOX MAY CHANGE SIZE CAUSING MOUSE TO BE OUTSIDE BOX.  
        // SEARCH RESULTS REMAIN OPEN WHEN SEARCH BAR IS FOCUSED
        focusSearchBar()
    }

    return (searching || noResults || resultsWithData?.length > 0) ? (
        <div className={`${styles.search_results_container}`} onClick={() => focusSearchBar()}>
            {searching ? 
                <div className={styles.search_result_loader}>
                    <Loader />
                </div>
            : noResults ? 
                <div className={styles.no_search_results}>
                    <p>No Results</p>
                </div>
            :
                <>
                    {!viewingSuggestion && displayList?.length > 0 ?
                        <div className={styles.suggested_search_results}>

                            {displayList.map((name, i) => (
                                <p key={`suggestions-${i}`} onClick={() => handleClickSuggestion(name)}>{name}</p>
                            ))}

                        </div>
                    : null}

                    <div className={styles.search_result_wrapper} onClick={e => e.stopPropagation()}>
                        {resultsWithData.map((resultSection, i) => {
                            const [contentName, resultArray] = resultSection;
                            return (
                                <div className={styles.search_results_group} key={`group-${i}`}>
                                    <p className={styles.search_result_content_type} >{contentName}</p>
                                
                                    {resultArray.slice(0, 6).map((result, idx) => (
                                        <div className={styles.search_result_row} key={`result-${i}-${idx}`}>
                                            <div className={styles.search_result} onClick={() => handleClickResult(result, contentName)}>
                                                <img src={contentDictionary[contentName].icon ? contentDictionary[contentName].icon : ManageContentIcon} alt="IMS Logo" className={styles.single_link_icon} />
                                                
                                                <div>

                                                    <p>{contentDictionary[contentName].getName(result)}</p>

                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )
                        })}

                    </div>
                </>
            }
            
        </div>
    ) : null
}


const mapStateToProps = (state) => ({
    cmsSearch: state.cmsSearch
});

export default connect(mapStateToProps, {
    setSubRoute,
    getBuyersGuideByID
})(CMSSearchResults);
